import React, { useState, useEffect } from 'react';
import { usersAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import UserModal from './UserModal';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { isSuperAdmin } = useAuth();

  useEffect(() => {
    if (isSuperAdmin()) {
      loadUsers();
    }
  }, [isSuperAdmin]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await usersAPI.getAll();
      setUsers(response.data.items || response.data || []);
    } catch (error) {
      setError('Error al cargar usuarios: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = () => {
    setSelectedUser(null);
    setShowModal(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setShowModal(true);
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este usuario?')) {
      return;
    }

    try {
      await usersAPI.delete(userId);
      setSuccess('Usuario eliminado exitosamente');
      loadUsers();
    } catch (error) {
      setError('Error al eliminar usuario: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      await usersAPI.changeRole(userId, newRole);
      // Recargar la lista de usuarios
      loadUsers();
      console.log(`✅ Rol actualizado para usuario ${userId}: ${newRole}`);
    } catch (error) {
      console.error('❌ Error cambiando rol:', error);
      alert('Error al cambiar el rol del usuario');
    }
  };

  const handleModalClose = (shouldReload) => {
    setShowModal(false);
    setSelectedUser(null);
    if (shouldReload) {
      loadUsers();
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES');
  };

  const getRoleBadge = (role) => {
    const roleClasses = {
      'SuperAdmin': 'status-completed',
      'Administrador': 'status-completed',
      'Admin': 'status-in-progress',
      'Usuario': 'status-pending',
      'User': 'status-pending'
    };

    return (
      <span className={`status-badge ${roleClasses[role] || 'status-pending'}`}>
        {role}
      </span>
    );
  };

  if (!isSuperAdmin()) {
    return (
      <div className="container">
        <div className="alert alert-error">
          No tienes permisos para acceder a esta sección.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>Gestión de Usuarios</h1>
        <button 
          className="btn btn-primary"
          onClick={handleCreateUser}
        >
          ➕ Nuevo Usuario
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
          <button 
            onClick={() => setError('')}
            style={{ float: 'right', background: 'none', border: 'none', fontSize: '16px' }}
          >
            ×
          </button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          {success}
          <button 
            onClick={() => setSuccess('')}
            style={{ float: 'right', background: 'none', border: 'none', fontSize: '16px' }}
          >
            ×
          </button>
        </div>
      )}

      <div className="card">
        {users.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            <p>No hay usuarios registrados</p>
            <button 
              className="btn btn-primary"
              onClick={handleCreateUser}
            >
              Crear primer usuario
            </button>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Email</th>
                <th>Rol</th>
                <th>Estado</th>
                <th>Fecha Creación</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>{user.firstName} {user.lastName}</td>
                  <td>{user.email}</td>
                  <td>{getRoleBadge(user.role)}</td>
                  <td>
                    <span className={`status-badge ${user.isActive ? 'status-completed' : 'status-failed'}`}>
                      {user.isActive ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td>{formatDate(user.createdAt)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                      <button 
                        className="btn btn-secondary"
                        onClick={() => handleEditUser(user)}
                        style={{ padding: '3px 8px', fontSize: '11px' }}
                      >
                        ✏️ Editar
                      </button>
                      
                      <select 
                        value={user.role}
                        onChange={(e) => handleChangeRole(user.id, e.target.value)}
                        style={{ padding: '3px', fontSize: '11px', borderRadius: '3px' }}
                      >
                        <option value="Usuario">Usuario</option>
                        <option value="Admin">Admin</option>
                        <option value="SuperAdmin">SuperAdmin</option>
                      </select>
                      
                      <button 
                        className="btn btn-danger"
                        onClick={() => handleDeleteUser(user.id)}
                        style={{ padding: '3px 8px', fontSize: '11px' }}
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <UserModal 
          user={selectedUser}
          onClose={handleModalClose}
        />
      )}
    </div>
  );
};

export default UserManagement;